import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { getReferralsWithJobInfo, type ReferralWithJobInfo } from "@/api/referrals";
import { ReferralStatus, RecruitmentStatus, type Job, type UserStats } from "@/lib/schema";
import { ClientReferralBanner } from "./ClientReferralBanner";
import { StatsCard } from "./StatsCard";
import { AvailableJobsSection } from "@/components/jobs";
import { ReferralsSection } from "@/components/referrals";
import { LeaderboardSection } from "./LeaderboardSection";
import { getClientReferralBonusAmount } from "@/api/referralSettings";
import { getLeaderboard } from "@/api/stats";

// Import SVG icons
import iconTotalReferrals from "@/assets/dashboard/icon_totalreferrals.svg";
import iconInterviewing from "@/assets/dashboard/icon_interviewing.svg";
import iconHired from "@/assets/dashboard/icon_hired.svg";
import iconRewardsEarned from "@/assets/dashboard/icon_rewardsearned.svg";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { formatRewardAmount } from "@/lib/textUtils";

interface JobListing {
  id: string;
  title: string;
  rewardInAirPoints?: number;
  location?: string;
  recruitmentStatus?: RecruitmentStatus;
}

export function ReferrerDashboard() {
  const { userData, currentUser } = useAuth();

  const { data: stats } = useQuery<UserStats>({
    queryKey: ["/api/users", userData?.id, "stats"],
    enabled: !!userData?.id && !!currentUser,
  });

  // Get client referral bonus amount from settings
  const { data: clientReferralBonus = 20000 } = useQuery<number>({
    queryKey: ["referralSettings", "clientReferralBonus"],
    queryFn: getClientReferralBonusAmount,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Get active jobs for available jobs section
  const { data: availableJobsData = [], isLoading: jobsLoading } = useQuery<Job[]>({
    queryKey: ["/api/jobs/available"],
    enabled: !!currentUser,
  });

  const { data: referrals = [], isLoading: isLoadingReferrals } = useQuery<ReferralWithJobInfo[]>({
    queryKey: ["referrals", userData?.id],
    queryFn: () => getReferralsWithJobInfo({ referrerId: userData?.id }),
    enabled: !!userData?.id && !!currentUser,
  });

  // Fetch leaderboard data
  const { data: leaderboardData = [], isLoading: isLoadingLeaderboard } = useQuery({
    queryKey: ["/api/leaderboard"],
    queryFn: () => getLeaderboard(5),
    enabled: !!currentUser,
  });

  // Calculate interviewing count from referrals data
  const interviewingCount = referrals.filter(ref => 
    ref.status === ReferralStatus.INTERVIEWED || ref.status === ReferralStatus.SHORTLISTED
  ).length;

  // Transform the job data to match our interface and limit to 4 jobs
  const totalJobs = availableJobsData.length;
  const availableJobs: JobListing[] = availableJobsData.slice(0, 4).map((job) => ({
    id: job.id,
    title: job.title,
    rewardInAirPoints: job.rewardInAirPoints,
    location: job.location,
    recruitmentStatus: job.recruitmentStatus
  }));

  // Transform leaderboard data to match LeaderboardSection props
  const leaderboard = leaderboardData.map((user, index) => ({
    rank: index + 1,
    name: user.name,
    amount: user.totalEarnings
  }));

  if (!userData) return null;


  return (
    <div className="min-h-screen page-gradient">
      <Sidebar />
      {/* Content with proper responsive margins for mobile and desktop */}
      <div className="lg:ml-56 p-4 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <DashboardHeader />

        {/* Client Referral Banner */}
        <ClientReferralBanner reward={clientReferralBonus} />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
          <StatsCard
            title="TOTAL REFERRALS"
            value={stats?.totalReferrals || 0}
            icon={iconTotalReferrals}
            testId="card-total-referrals"
          />

          <StatsCard
            title="INTERVIEWING"
            value={interviewingCount}
            icon={iconInterviewing}
            testId="card-interviewing"
          />

          <StatsCard
            title="HIRED"
            value={stats?.successfulHires || 0}
            icon={iconHired}
            testId="card-hired"
          />

          <StatsCard
            title="REWARDS EARNED"
            value={`R${formatRewardAmount(stats?.totalEarnings)}`}
            icon={iconRewardsEarned}
            variant="dark"
            testId="card-rewards"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Available Jobs */}
          <AvailableJobsSection 
            jobs={availableJobs}
            isLoading={jobsLoading}
            maxJobs={3}
            totalJobs={totalJobs}
            className=""
          />

          {/* Your Referrals */}
          <ReferralsSection 
            referrals={referrals}
            isLoading={isLoadingReferrals}
            maxReferrals={3}
          />
        </div>

        {/* Top Referrers Leaderboard */}
        <LeaderboardSection 
          leaderboard={leaderboard}
          isLoading={isLoadingLeaderboard}
          maxEntries={5}
        />
      </div>
    </div>
  );
}